import axios from "axios";

// Every call here takes the staff JWT explicitly, because relying on axios
// defaults loses a race on cold loads.
//
// The server's `middleware/auth.js` authenticates on `x-auth-token` and ignores
// `Authorization`. `setupAxios` only ever sets `Authorization: Bearer` and is
// commented out in `src/index.js` anyway, so the one thing that makes these
// endpoints work is `axios.defaults.headers.common["x-auth-token"]`, set as a
// side effect of `getUserByToken()` inside the rehydrate saga (`authRedux.js`,
// UserRequested). That saga resolves *after* mount, so anything fetching in
// between goes out unauthenticated and comes back 401 — which reads to staff as
// "Your session has expired" on a perfectly good session. A pasted deep link or
// a hard refresh lands squarely in that window.
//
// `authToken` itself is persisted and PersistGate blocks render until rehydrate
// finishes, so the store has the token at first mount even though axios does
// not. Reading it from there per request is what EnrollmentPauseDashboardPage
// and BranchCourseCustomizationList already do; this matches them rather than
// touching the app-wide plumbing.
export const BATCHES_URL = "api/batches";
export const BADGES_URL = "api/attendance-kiosk/badges";

/**
 * Throws rather than falling back to `axios.defaults`. A silent fallback is
 * precisely the cold-load 401 above: it would work in every warm test and fail
 * only on the paths nobody clicks through by hand. Callers guard on the token
 * before fetching, so this is a backstop, not a control path.
 */
export function authHeaders(authToken) {
  if (!authToken) {
    throw new Error("Cannot call the kiosk API without a staff auth token.");
  }
  return { headers: { "x-auth-token": authToken } };
}

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** GET api/batches/branch/:id — note the envelope key is `result`, singular. */
export async function fetchBatches(branchId, authToken) {
  const response = await axios.get(
    `${BATCHES_URL}/branch/${branchId}`,
    authHeaders(authToken)
  );
  const rows = response.data && response.data.result;
  return Array.isArray(rows)
    ? rows.filter((row) => row && typeof row.batch_id === "number")
    : [];
}

/**
 * POST api/attendance-kiosk/badges — by batch, or by explicit student ids.
 * Minting is idempotent per student, so reprinting a class never invalidates a
 * card a student is already carrying.
 */
export async function fetchBadges(selector, authToken) {
  const response = await axios.post(
    BADGES_URL,
    selector,
    authHeaders(authToken)
  );
  const rows = response.data && response.data.data;
  return Array.isArray(rows) ? rows : [];
}

/**
 * How many badge calls are allowed to be in flight at once for a whole-day
 * fetch. A branch's busiest day is a dozen or so classes; four at a time keeps
 * that under a couple of seconds without opening twelve connections to an
 * endpoint that writes.
 */
const BADGE_FETCH_CONCURRENCY = 4;

/**
 * Order for a merged sheet: first name, then last name, then user_id.
 *
 * This is exactly the order the server already returns a single class in
 * (`ORDER BY u.firstname, u.lastname` in POST /badges), so a whole-day sheet
 * reads the same way as the class sheets staff already hand out, and the same
 * way the branch's own lists read. `sensitivity: "base"` so "de Souza" and
 * "De Souza" do not land pages apart.
 */
export function compareBadgeRows(a, b) {
  const byFirst = (a.firstname || "").localeCompare(b.firstname || "", undefined, {
    sensitivity: "base",
  });
  if (byFirst !== 0) return byFirst;
  const byLast = (a.lastname || "").localeCompare(b.lastname || "", undefined, {
    sensitivity: "base",
  });
  if (byLast !== 0) return byLast;
  return (a.user_id || 0) - (b.user_id || 0);
}

/**
 * Badges for several classes at once, merged into one sheet.
 *
 * One `{ batch_id }` call per class rather than one `{ student_user_ids }`
 * call for all of them, for two reasons:
 *
 *   - The id list would have to be built client-side from `batch.students`,
 *     which the branch fetch populates from `student_batch` with `active_id =
 *     1` only. POST /badges takes `active_id IN (1, 6)`, so any student in the
 *     other live enrolment state would be silently missing from the printed
 *     sheet. The server knows the roster; the client should not second-guess
 *     it.
 *   - The id path is all-or-nothing on branch containment by design — one id
 *     outside the caller's branches rejects the whole request — so a failure
 *     could not be attributed to a class. Per-class calls can say exactly
 *     which classes are missing, which is the difference between an honest
 *     short sheet and a silent one.
 *
 * Never throws for a partial failure: it returns what came back plus a
 * `failures` list, and the caller decides how loudly to say so. It is the
 * caller's job to make sure this is only ever reached by a deliberate press —
 * every call here mints a durable badge code for any student who lacks one.
 */
export async function fetchBadgesForBatches(batchIds, authToken) {
  const ids = [...batchIds];
  const rowsByBatch = new Map();
  const failuresByBatch = new Map();

  let cursor = 0;
  const worker = async () => {
    while (cursor < ids.length) {
      const batchId = ids[cursor];
      cursor += 1;
      try {
        rowsByBatch.set(
          batchId,
          await fetchBadges({ batch_id: batchId }, authToken)
        );
      } catch (error) {
        failuresByBatch.set(
          batchId,
          describeBadgeError(
            error,
            `Could not fetch badges for class #${batchId}.`
          )
        );
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(BADGE_FETCH_CONCURRENCY, ids.length) }, () =>
      worker()
    )
  );

  // One student, one badge, one card. A student enrolled in two classes on the
  // same day comes back from both calls, and the second copy is dropped rather
  // than printed: `user_id` is the identity, not `badge_code` — a student can
  // legitimately hold more than one live badge row, so deduplicating on the
  // code would let the same child through twice.
  const byUser = new Map();
  let duplicates = 0;
  ids.forEach((batchId) => {
    (rowsByBatch.get(batchId) || []).forEach((row) => {
      if (!row || row.user_id === null || row.user_id === undefined) return;
      if (byUser.has(row.user_id)) {
        duplicates += 1;
        return;
      }
      byUser.set(row.user_id, row);
    });
  });

  return {
    badges: [...byUser.values()].sort(compareBadgeRows),
    duplicates,
    // In the order the caller asked for, so the message naming them reads in
    // the same order as the dropdown they came from.
    failures: ids
      .filter((batchId) => failuresByBatch.has(batchId))
      .map((batchId) => ({ batchId, message: failuresByBatch.get(batchId) })),
    fetchedCount: ids.length - failuresByBatch.size,
    requestedCount: ids.length,
  };
}

/**
 * Turn an axios failure into something staff can act on.
 *
 * Every attendance-kiosk endpoint is authorised by role and branch on the
 * server, so a 403 is an ordinary outcome for staff without the right role —
 * it has to read as a sentence, not as an empty sheet or a silent no-op.
 *
 * `forbiddenMessage` lets a caller name what it was denied ("view attendance",
 * "print badges") when the server sends no message of its own; omitted, the
 * wording stays exactly as it was for the badge screens.
 */
export function describeBadgeError(error, fallback, forbiddenMessage) {
  const response = error && error.response;
  const status = response && response.status;
  const data = response && response.data;
  const serverMessage =
    data && typeof data === "object" ? data.message || data.error : null;

  if (status === 403) {
    return (
      serverMessage ||
      forbiddenMessage ||
      "You do not have permission to print badges for this branch. Ask an Admin or Branch Head."
    );
  }
  if (status === 401) {
    return "Your session has expired. Please sign in again.";
  }
  if (typeof serverMessage === "string" && serverMessage.trim()) {
    return serverMessage;
  }
  if (error && error.message === "Network Error") {
    return "Could not reach the server. Check the network and try again.";
  }
  return fallback;
}

/** `batch.day` can be comma-separated ("Monday,Wednesday"). */
export function batchDays(batch) {
  return (batch.day || "")
    .split(",")
    .map((day) => day.trim())
    .filter(Boolean);
}

/** "16:30:00" -> "4:30 PM". Tolerates ISO timestamps and junk. */
export function formatTime(value) {
  if (!value) return "";
  const source = value.includes("T") ? value.slice(11) : value;
  const match = /^(\d{1,2}):(\d{2})/.exec(source);
  if (!match) return value;
  const hours = Number(match[1]);
  const minutes = match[2];
  if (!Number.isFinite(hours)) return value;
  const suffix = hours < 12 ? "AM" : "PM";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${minutes} ${suffix}`;
}

/**
 * Compose a human label for a batch. There is no batch name column, so this
 * mirrors what the class table renders: day(s), time range, course codes.
 */
export function formatBatchLabel(batch) {
  const days = batchDays(batch)
    .map((day) => day.slice(0, 3))
    .join("/");
  const time = `${formatTime(batch.start_time)}–${formatTime(
    batch.end_time
  )}`;
  const courses = (batch.courses || [])
    .filter((course) => course.show_ind !== 0)
    .map((course) => course.course_code)
    .filter(Boolean)
    .join(", ");
  return [days, time, courses || batch.program_name].filter(Boolean).join("  ·  ");
}

export function sortWeekdays(days) {
  return [...days].sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b));
}
